import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import api from '../../utils/api';

// Color per channel — tries to mirror brand colors of each platform so the
// pill is recognizable at a glance.
const CHANNEL_COLORS = {
  email: '#3b82f6',
  whatsapp: '#25D366',
  sms: '#f59e0b',
  portal: '#8b5cf6',
  phone: '#10b981',
  telegram: '#0088cc',
  manual: '#6b7280',
};

const CHANNEL_LABELS_FALLBACK = {
  email: 'Email', whatsapp: 'WhatsApp', sms: 'SMS', portal: 'Portal',
  phone: 'Phone', telegram: 'Telegram', manual: 'Manual',
};

export const meta = {
  id: 'ticket_channels',
  category: 'tickets',
  titleKey: 'dashboard.widgets.ticketChannels',
  descriptionKey: 'dashboard.widgets.ticketChannelsDesc',
  defaultSize: 'md',
  icon: MessageSquare,
};

export default function TicketChannelsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [days, setDays] = useState(7);

  const { data } = useQuery({
    queryKey: ['dashboard-ticket-channels', days],
    queryFn: () => api.get('/dashboard/ticket-channels', { params: { days } }).then(r => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  const channels = data?.channels || [];
  const total = channels.reduce((s, c) => s + (parseInt(c.count) || 0), 0);

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MessageSquare size={16} />
          {t('dashboard.widgets.ticketChannels')}
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          style={{ fontSize: 11, padding: '2px 6px', background: 'var(--c-surface)', color: 'var(--c-text-muted)', border: '1px solid var(--c-border)', borderRadius: 6 }}
        >
          <option value={1}>{t('dashboard.widgets.channelsLast24h', '24h')}</option>
          <option value={7}>{t('dashboard.widgets.channelsLast7d', '7d')}</option>
          <option value={30}>{t('dashboard.widgets.channelsLast30d', '30d')}</option>
        </select>
      </div>
      {total === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
      ) : (
        <div style={{ padding: '4px 16px 14px' }}>
          {/* Stacked horizontal bar */}
          <div style={{
            display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden',
            marginBottom: 12, background: 'var(--c-surface-2)',
          }}>
            {channels.map(c => {
              const pct = (parseInt(c.count) / total) * 100;
              return (
                <div
                  key={c.channel}
                  style={{ width: `${pct}%`, background: CHANNEL_COLORS[c.channel] || '#6b7280' }}
                  title={`${c.channel}: ${c.count}`}
                />
              );
            })}
          </div>
          {/* Per-channel rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {channels.map(c => {
              const count = parseInt(c.count) || 0;
              const pct = Math.round((count / total) * 100);
              const color = CHANNEL_COLORS[c.channel] || '#6b7280';
              return (
                <div
                  key={c.channel}
                  onClick={() => navigate(`/tickets?channel=${encodeURIComponent(c.channel)}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    cursor: 'pointer', padding: '4px 6px', borderRadius: 4,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, flex: 1, textTransform: 'capitalize' }}>
                    {CHANNEL_LABELS_FALLBACK[c.channel] || c.channel}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--c-text-muted)', minWidth: 32, textAlign: 'right' }}>
                    {pct}%
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, minWidth: 32, textAlign: 'right' }}>
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
